/**
 * Iter 85.1 / Phase E6 Sprint 1 — Manager Team Activity Dashboard (D41).
 *
 * Surfaces direct-report activity to managers (anyone with at least one
 * employee naming them in `reporting_manager_employee_id`).
 *
 * RBAC: manager:read OR workforce:read
 */
import { useCallback, useEffect, useState } from "react";
import { ArrowsClockwise } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import WorkforcePrivacyBanner from "@/components/workforce/WorkforcePrivacyBanner";

const CLASS_BADGE = {
    ACTIVE: "bg-emerald-100 text-emerald-800 border-emerald-200",
    UNDERUTILIZED: "bg-amber-100 text-amber-800 border-amber-200",
    INACTIVE: "bg-rose-100 text-rose-800 border-rose-200",
    OVERLOADED: "bg-orange-100 text-orange-800 border-orange-200",
    UNCLASSIFIED: "bg-slate-100 text-slate-600 border-slate-200",
};

const today = () => new Date().toISOString().slice(0, 10);
const hrs = (m) => (m == null ? "—" : `${(m / 60).toFixed(1)}h`);

export default function ManagerWorkforceDashboard() {
    const [asOfDate, setAsOfDate] = useState(today());
    const [classFilter, setClassFilter] = useState("");
    const [snap, setSnap] = useState(null);
    const [loading, setLoading] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (asOfDate) params.set("as_of_date", asOfDate);
            if (classFilter) params.set("classification", classFilter);
            const { data } = await api.get(`/workforce/manager/team?${params.toString()}`);
            setSnap(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load team activity"));
        } finally {
            setLoading(false);
        }
    }, [asOfDate, classFilter]);

    useEffect(() => { reload(); }, [reload]);

    const b = snap?.buckets || {};

    return (
        <div className="p-6 space-y-4" data-testid="manager-workforce-dashboard">
            <PageHeader
                title="Team Activity"
                subtitle="Your direct reports' daily activity · D41 — privacy-safe · no opaque scoring"
                actions={
                    <div className="flex gap-2 items-end">
                        <div>
                            <Label className="text-xs">As-of</Label>
                            <Input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)}
                                className="w-36" data-testid="manager-team-as-of-input" />
                        </div>
                        <Button size="sm" variant="outline" onClick={reload} disabled={loading}
                            data-testid="manager-team-refresh-btn">
                            <ArrowsClockwise size={14} className="mr-1" /> Refresh
                        </Button>
                    </div>
                }
            />

            <WorkforcePrivacyBanner />

            {/* UAT-08 (2026-07-14) · No-telemetry empty-state banner.
                Fires when the reporting hierarchy resolved cleanly but
                every rollup came back with no meaningful activity signal
                (i.e. no ACTIVE / UNDERUTILIZED / OVERLOADED classification).
                Renders instead of leaving the operator staring at a table
                of "0h · 0h · 0% · UNCLASSIFIED / OFFLINE" rows they can't
                action on. */}
            {snap && (snap.items || []).length > 0
                && ((b.ACTIVE || 0) + (b.UNDERUTILIZED || 0) + (b.OVERLOADED || 0)) === 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="manager-team-no-telemetry-banner">
                    <div className="font-semibold mb-0.5">Workforce telemetry unavailable for {snap.as_of_date}</div>
                    <div className="text-amber-800">
                        Your team resolved cleanly ({snap.items.length} direct report{snap.items.length === 1 ? "" : "s"}) but no activity signal is available for this date. Activity metrics populate once the Desktop Agent is deployed for the team. Contact your IT admin to enable Workforce Analytics.
                    </div>
                </div>
            )}

            {snap?.manager_employee_id && (
                <div className="text-[10px] text-slate-500 font-mono">
                    manager_employee_id={snap.manager_employee_id} · {snap.items?.length || 0} direct reports
                </div>
            )}

            {/* Buckets — clickable filters */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="manager-team-buckets">
                {["ACTIVE", "UNDERUTILIZED", "INACTIVE", "OVERLOADED", "UNCLASSIFIED"].map((k) => (
                    <button key={k}
                        onClick={() => setClassFilter(classFilter === k ? "" : k)}
                        className={`border rounded p-3 text-left ${
                            classFilter === k ? "ring-2 ring-blue-400" : ""
                        } ${CLASS_BADGE[k]}`}
                        data-testid={`manager-bucket-${k}`}>
                        <div className="text-[10px] uppercase tracking-wider">{k}</div>
                        <div className="text-2xl font-mono font-bold mt-1">{b[k] || 0}</div>
                    </button>
                ))}
            </div>

            <div className="border rounded">
                <table className="w-full text-xs" data-testid="manager-team-table">
                    <thead className="bg-slate-50 border-b uppercase text-slate-600 text-[10px] sticky top-0">
                        <tr>
                            <th className="text-left px-2 py-1">Code</th>
                            <th className="text-left px-2 py-1">Name</th>
                            <th className="text-left px-2 py-1">Dept</th>
                            <th className="text-left px-2 py-1">Login</th>
                            <th className="text-left px-2 py-1">Logout</th>
                            <th className="text-right px-2 py-1">Logged-in</th>
                            <th className="text-right px-2 py-1">Active</th>
                            <th className="text-right px-2 py-1">Idle</th>
                            <th className="text-right px-2 py-1">Break</th>
                            <th className="text-right px-2 py-1">Activity %</th>
                            <th className="text-left px-2 py-1">Classification</th>
                            <th className="text-left px-2 py-1">Reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(!snap || (snap.items || []).length === 0) && (
                            <tr><td colSpan="12" className="text-center text-slate-400 p-6">
                                No direct reports&apos; activity for the selected filters
                            </td></tr>
                        )}
                        {(snap?.items || []).map((it) => (
                            <tr key={it.employee_id} className="border-b hover:bg-slate-50/50"
                                data-testid={`manager-row-${it.employee_id}`}>
                                <td className="px-2 py-1 font-mono text-[10px]">{it.employee_code}</td>
                                <td className="px-2 py-1">{it.employee_name}</td>
                                <td className="px-2 py-1 text-[10px]">{it.department}</td>
                                <td className="px-2 py-1 font-mono text-[10px]">{(it.login_time || "—").slice(11, 16)}</td>
                                <td className="px-2 py-1 font-mono text-[10px]">{(it.logout_time || "—").slice(11, 16)}</td>
                                <td className="px-2 py-1 text-right font-mono">{hrs(it.total_logged_in_minutes)}</td>
                                <td className="px-2 py-1 text-right font-mono">{hrs(it.total_active_minutes)}</td>
                                <td className="px-2 py-1 text-right font-mono">{hrs(it.total_idle_minutes)}</td>
                                <td className="px-2 py-1 text-right font-mono">{hrs(it.total_break_minutes)}</td>
                                <td className="px-2 py-1 text-right font-mono font-semibold">{it.activity_pct}%</td>
                                <td className="px-2 py-1">
                                    <Badge className={CLASS_BADGE[it.classification] || ""}>
                                        {it.classification}
                                    </Badge>
                                </td>
                                <td className="px-2 py-1 text-[10px] text-slate-600 max-w-xs">
                                    {it.classification_reason}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
